import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { MeasureType, ReportData } from '../types';
import { ReportDimensionSourceDTO } from '../types/report-dimension';
import { formatPivotKey, PIVOT_SEP } from '../utils/chart-data';
import { formatMeasureValue } from '../utils/format-measure';
import { CustomDimensionTag } from './custom-dimension-tag';

const TYPE_UNITS: Partial<Record<string, string>> = {
  [MeasureType.HOURS]: 'h',
  [MeasureType.PERCENT]: '%',
};

const MeasureHeader = ({ label, unit, type }: { label: string; unit?: string; type?: string }) => {
  const displayUnit = unit ?? (type ? TYPE_UNITS[type] : undefined);
  return (
    <span>
      {label}
      {displayUnit && <span className="ml-1 font-normal opacity-60">{displayUnit}</span>}
    </span>
  );
};

const coerce = (v: unknown): number | string | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : v;
  }
  return null;
};

interface ColMeta {
  isText?: boolean;
  groupBorder?: boolean;
  cellClassName?: string;
}

type RowRecord = Record<string, number | string | null>;

interface ElementTableProps {
  data: ReportData;
  maxContentHeight?: number;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

export const ElementTable = ({
  data,
  maxContentHeight,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: ElementTableProps) => {
  const { t } = useTranslation('authenticated.reports');
  const emDash = t('common.emDash');

  const isPivot = !!data.pivotDimension && (data.pivotValues?.length ?? 0) > 0;

  const columns = useMemo<ColumnDef<RowRecord>[]>(() => {
    const pvs = data.pivotValues ?? [];
    const pivotDimKey = data.pivotDimension?.key;

    const dimCols = data.dimensions.map<ColumnDef<RowRecord>>(d => ({
      id: d.key,
      accessorFn: (row: RowRecord) => row[d.displayKey] ?? null,
      header:
        d.source === ReportDimensionSourceDTO.CUSTOM_ATTRIBUTE
          ? () => (
              <span className="inline-flex items-center gap-1.5">
                {d.label}
                <CustomDimensionTag />
              </span>
            )
          : d.label,
      cell: ({ getValue }) => {
        const v = getValue();
        return v != null ? String(v) : emDash;
      },
      meta: { isText: true } as ColMeta,
    }));

    if (!isPivot) {
      return [
        ...dimCols,
        ...data.measures.map<ColumnDef<RowRecord>>(m => ({
          id: m.key,
          accessorFn: (row: RowRecord) => row[m.key] ?? null,
          header: () => <MeasureHeader label={m.label} unit={m.unit} type={m.type} />,
          cell: ({ getValue }) => formatMeasureValue(coerce(getValue()), m.type, emDash),
        })),
      ];
    }

    return [
      ...dimCols,
      ...data.measures.map<ColumnDef<RowRecord>>((m, mIdx) => ({
        id: m.key,
        header: () => <MeasureHeader label={m.label} unit={m.unit} type={m.type} />,
        columns: pvs.map((pv, pvIdx) => {
          const colKey = `${m.key}${PIVOT_SEP}${pv.key}`;
          const isLastInGroup = pvIdx === pvs.length - 1;
          const isLastMeasure = mIdx === data.measures.length - 1;
          return {
            id: colKey,
            accessorFn: (row: RowRecord) => row[colKey] ?? null,
            header: formatPivotKey(pv.label, pivotDimKey),
            cell: ({ getValue }: { getValue: () => unknown }) =>
              formatMeasureValue(coerce(getValue()), m.type, emDash),
            meta: { groupBorder: isLastInGroup && !isLastMeasure } as ColMeta,
          };
        }),
      })),
    ];
  }, [data, isPivot, emDash]);

  const rawRows = useMemo(() => data.rows.map(r => r.columns as RowRecord), [data.rows]);

  const { combinedRows, groupSummaryIndices } = useMemo(() => {
    const summaryRows = data.groupSummaryRows;
    if (!summaryRows?.length || data.dimensions.length === 0) {
      return { combinedRows: rawRows, groupSummaryIndices: new Set<number>() };
    }
    const firstDimIdKey = data.dimensions[0].idKey;
    const summaryByKey = new Map<unknown, RowRecord>();
    for (const sr of summaryRows) summaryByKey.set(sr[firstDimIdKey], sr as RowRecord);

    const combined: RowRecord[] = [];
    const summaryIndices = new Set<number>();
    let prevKey: unknown = undefined;

    for (const row of rawRows) {
      const curKey = row[firstDimIdKey];
      if (prevKey !== undefined && curKey !== prevKey) {
        const summary = summaryByKey.get(prevKey);
        if (summary) {
          summaryIndices.add(combined.length);
          combined.push(summary);
        }
      }
      combined.push(row);
      prevKey = curKey;
    }
    if (prevKey !== undefined) {
      const summary = summaryByKey.get(prevKey);
      if (summary) {
        summaryIndices.add(combined.length);
        combined.push(summary);
      }
    }
    return { combinedRows: combined, groupSummaryIndices: summaryIndices };
  }, [rawRows, data.groupSummaryRows, data.dimensions]);

  const totalsRow = data.totalsRow ? (data.totalsRow as RowRecord) : null;

  const table = useReactTable({ data: combinedRows, columns, getCoreRowModel: getCoreRowModel() });

  const totalCols = table.getVisibleLeafColumns().length;

  const scrollDivRef = useRef<HTMLDivElement>(null);
  const footerScrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [leafColWidths, setLeafColWidths] = useState<number[]>([]);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scroll = scrollDivRef.current;
    if (!sentinel || !scroll || !hasNextPage || !onLoadMore) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      { root: scroll, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, onLoadMore]);

  // Measure leaf column widths after every render so the footer table stays aligned.
  // State comparison prevents infinite re-render loops; intentionally no dep array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (!totalsRow || !tableRef.current) return;
    const tbl = tableRef.current;
    const headerRows = tbl.querySelectorAll('thead tr');
    if (headerRows.length === 0) return;
    const lastHeaderRow = headerRows[headerRows.length - 1];
    const ths = Array.from(lastHeaderRow.querySelectorAll('th'));
    const newWidths = ths.map(th => th.getBoundingClientRect().width);
    const newTotal = tbl.getBoundingClientRect().width;
    setLeafColWidths(prev =>
      prev.length === newWidths.length && prev.every((w, i) => Math.abs(w - newWidths[i]) < 0.5)
        ? prev
        : newWidths
    );
    setTableWidth(prev => (Math.abs(prev - newTotal) < 0.5 ? prev : newTotal));
  });

  const handleScroll = () => {
    if (footerScrollRef.current && scrollDivRef.current) {
      footerScrollRef.current.scrollLeft = scrollDivRef.current.scrollLeft;
    }
  };

  if (totalCols === 0) {
    return <p className="text-sm text-muted-foreground">{t('element.noColumnsAvailable')}</p>;
  }

  const headerGroups = table.getHeaderGroups();
  const hasSubHeaders = headerGroups.length > 1;

  return (
    <div
      className="flex w-full flex-col overflow-hidden rounded-lg border"
      style={maxContentHeight != null ? { height: maxContentHeight } : undefined}
    >
      {/* Scrollable area: header + rows */}
      <div ref={scrollDivRef} className="min-h-0 flex-1 overflow-auto" onScroll={handleScroll}>
        <table ref={tableRef} className="w-full caption-bottom border-collapse text-sm">
          <thead className="sticky top-0 z-10">
            {headerGroups.map((hg, hgIdx) => {
              const isGroupRow = hgIdx === 0 && hasSubHeaders;
              const isLeafRow = !isGroupRow;
              return (
                <tr key={hg.id} className={cn('bg-muted', isGroupRow && 'border-b')}>
                  {hg.headers.map((header, colIdx) => {
                    const meta = header.column.columnDef.meta as ColMeta | undefined;
                    const isFirst = colIdx === 0;
                    const isGroup = !header.isPlaceholder && header.colSpan > 1;

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={cn(
                          'px-3 text-xs font-semibold tracking-wide text-muted-foreground whitespace-nowrap select-none',
                          isLeafRow ? 'border-b py-2' : 'py-2.5',
                          isGroup ? 'text-center' : meta?.isText ? 'text-left' : 'text-right',
                          isFirst && 'sticky left-0 z-20 bg-muted text-left',
                          meta?.groupBorder && 'border-r'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody className="bg-background">
            {rawRows.length === 0 ? (
              <tr>
                <td colSpan={totalCols} className="py-8 text-center text-sm text-muted-foreground">
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              <>
                {table.getRowModel().rows.map(row => {
                  const isGroupSummary = groupSummaryIndices.has(row.index);
                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        'border-b last:border-b-0',
                        isGroupSummary ? 'bg-muted' : 'group hover:bg-muted/40'
                      )}
                    >
                      {row.getVisibleCells().map((cell, cellIdx) => {
                        const meta = cell.column.columnDef.meta as ColMeta | undefined;
                        const isFirst = cellIdx === 0;
                        return (
                          <td
                            key={cell.id}
                            className={cn(
                              'px-3 py-2.5 text-sm',
                              meta?.isText || isFirst ? 'text-left' : 'text-right tabular-nums',
                              isFirst && 'sticky left-0 z-[1]',
                              isFirst &&
                                (isGroupSummary
                                  ? 'bg-muted'
                                  : 'bg-background font-medium group-hover:bg-muted/40'),
                              isGroupSummary && 'font-semibold',
                              meta?.groupBorder && 'border-r'
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {isFetchingNextPage &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="border-b">
                      {table.getVisibleLeafColumns().map((col, colIdx) => (
                        <td
                          key={col.id}
                          className={cn(
                            'px-3 py-2.5',
                            colIdx === 0 && 'sticky left-0 z-[1] bg-background'
                          )}
                        >
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}
              </>
            )}
          </tbody>
        </table>
        {hasNextPage && <div ref={sentinelRef} className="h-1 shrink-0" />}
      </div>

      {/* Non-scrolling totals footer — outside the scroll area so it's always visible */}
      {totalsRow && leafColWidths.length > 0 && (
        <div className="shrink-0 border-t-2">
          <div ref={footerScrollRef} className="overflow-hidden">
            <table
              className="caption-bottom border-collapse text-sm"
              style={{ tableLayout: 'fixed', width: tableWidth || '100%' }}
            >
              <colgroup>
                {leafColWidths.map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <tbody>
                <tr className="bg-muted">
                  {table.getVisibleLeafColumns().map((col, colIdx) => {
                    const meta = col.columnDef.meta as ColMeta | undefined;
                    const isFirst = colIdx === 0;
                    const measureDef = data.measures.find(
                      m => m.key === col.id || (isPivot && col.id.startsWith(m.key + PIVOT_SEP))
                    );
                    const val = totalsRow[col.id];
                    const content = isFirst
                      ? t('common.total')
                      : val != null && measureDef
                        ? formatMeasureValue(coerce(val), measureDef.type, emDash)
                        : null;
                    return (
                      <td
                        key={col.id}
                        className={cn(
                          'px-3 py-2.5 text-sm font-semibold',
                          meta?.isText || isFirst ? 'text-left' : 'text-right tabular-nums',
                          meta?.groupBorder && 'border-r'
                        )}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
